import React, { useState } from "react";
import axios from "axios";
import {
    Paper,
    Group,
    Text,
    Button,
    Table,
    Badge,
    Modal,
    Stack,
    TextInput,
    Switch,
    ActionIcon,
    Tooltip,
    Select,
    NumberInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import type {
    TransportRouteLocationDto,
    CreateTransportRouteLocationRequest,
} from "../types";

const apiUrl = process.env.REACT_APP_API_URL;
const RED = "#ff5e5e";

type Props = {
    eventId: number | null;
    locations: TransportRouteLocationDto[];
    refreshLocations: () => Promise<void>;
    onDeleteLocation: (id: number) => void;
};

export default function TransportRouteLocationsTab({
    eventId,
    locations,
    refreshLocations,
    onDeleteLocation,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TransportRouteLocationDto | null>(null);

    const [nameDK, setNameDK] = useState("");
    const [nameEN, setNameEN] = useState("");
    const [locationType, setLocationType] = useState("stop");
    const [sortOrder, setSortOrder] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setNameDK("");
        setNameEN("");
        setLocationType("stop");
        setSortOrder(0);
        setIsActive(true);
        setModalOpen(true);
    };

    const openEdit = (location: TransportRouteLocationDto) => {
        setEditing(location);
        setNameDK(location.nameDK ?? "");
        setNameEN(location.nameEN ?? "");
        setLocationType(location.locationType ?? "stop");
        setSortOrder(location.sortOrder ?? 0);
        setIsActive(location.isActive);
        setModalOpen(true);
    };

    const save = async () => {
        if (!eventId) return;

        if (!nameDK.trim()) {
            showNotification({
                title: "Mangler",
                message: "Navn (DK) er påkrævet",
                color: "red",
            });
            return;
        }

        try {
            setSaving(true);

            if (!editing) {
                const payload: CreateTransportRouteLocationRequest = {
                    eventID: eventId,
                    nameDK: nameDK.trim(),
                    nameEN: nameEN.trim() || null,
                    locationType: locationType || "stop",
                    sortOrder,
                };

                await axios.post(`${apiUrl}/api/transport-route/admin/locations`, payload);

                showNotification({
                    title: "Gemt",
                    message: "Location oprettet",
                });
            } else {
                await axios.put(
                    `${apiUrl}/api/transport-route/admin/locations/${editing.transportRouteLocationID}`,
                    {
                        nameDK: nameDK.trim(),
                        nameEN: nameEN.trim() || null,
                        locationType: locationType || "stop",
                        sortOrder,
                        isActive,
                    }
                );

                showNotification({
                    title: "Gemt",
                    message: "Location opdateret",
                });
            }

            setModalOpen(false);
            await refreshLocations();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke gemme location",
                color: "red",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Group justify="space-between" mb="sm">
                <Text fw={700}>Locations</Text>

                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openCreate}
                    disabled={!eventId}
                    style={{ backgroundColor: RED }}
                >
                    Opret location
                </Button>
            </Group>

            <Paper withBorder p="md" radius="md">
                <Table.ScrollContainer minWidth={950}>
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Navn (DK)</Table.Th>
                                <Table.Th>Navn (EN)</Table.Th>
                                <Table.Th>Type</Table.Th>
                                <Table.Th>Sort</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {locations.map((location) => (
                                <Table.Tr key={location.transportRouteLocationID}>
                                    <Table.Td>{location.nameDK}</Table.Td>
                                    <Table.Td>{location.nameEN ?? "-"}</Table.Td>
                                    <Table.Td>{location.locationType}</Table.Td>
                                    <Table.Td>{location.sortOrder}</Table.Td>
                                    <Table.Td>
                                        <Badge
                                            color={location.isActive ? "green" : "gray"}
                                            variant="light"
                                        >
                                            {location.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group justify="flex-end" gap="xs">
                                            <Tooltip label="Rediger">
                                                <ActionIcon
                                                    variant="light"
                                                    onClick={() => openEdit(location)}
                                                >
                                                    <IconPencil size={16} />
                                                </ActionIcon>
                                            </Tooltip>

                                            <Tooltip label="Slet">
                                                <ActionIcon
                                                    color="red"
                                                    variant="light"
                                                    onClick={() =>
                                                        onDeleteLocation(location.transportRouteLocationID)
                                                    }
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? "Rediger location" : "Opret location"}
                centered
            >
                <Stack>
                    <TextInput
                        label="Navn (DK)"
                        value={nameDK}
                        onChange={(e) => setNameDK(e.currentTarget.value)}
                    />

                    <TextInput
                        label="Navn (EN)"
                        value={nameEN}
                        onChange={(e) => setNameEN(e.currentTarget.value)}
                    />

                    <Select
                        label="Type"
                        data={[
                            { value: "stop", label: "Stop" },
                            { value: "hotel", label: "Hotel" },
                            { value: "arena", label: "Arena" },
                            { value: "hub", label: "Hub" },
                        ]}
                        value={locationType}
                        onChange={(v) => setLocationType(v ?? "stop")}
                    />

                    <NumberInput
                        label="SortOrder"
                        value={sortOrder}
                        onChange={(v) => setSortOrder(Number(v) || 0)}
                    />

                    {editing && (
                        <Switch
                            label="Aktiv"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.currentTarget.checked)}
                        />
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={save} loading={saving}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}